import React, { useEffect, useState } from 'react';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  FormGroup,
  Label,
  Input,
  Spinner,
  FormFeedback,
} from 'reactstrap';

import ExhibitionService from '../../../Service/ExhibitionService';
import ExhibitionTypeService from '../../../Service/ExhibitionTypeService';
import { Exhibition } from './types';
import { toast } from 'react-toastify';
import { ExhibitionType } from '../ExhibitionType/types';


interface Props {
  isOpen: boolean;
  toggle: () => void;
  onSuccess: () => void;
  initialData: Exhibition | null;
}



const ExhibitionModal = ({ isOpen, toggle, onSuccess, initialData }: Props) => {
  const [form, setForm] = useState<Omit<Exhibition, 'id'>>({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    venue: '',
    exhibition_type: 0,
    phone_number: 0,
    email: '',
    status: true,
  });

  const [types, setTypes] = useState<ExhibitionType[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (initialData) {
      setForm({
        title: initialData.title,
        description: initialData.description,
        start_date: initialData.start_date,
        end_date: initialData.end_date,
        venue: initialData.venue,
        exhibition_type: initialData.exhibition_type,
        phone_number: initialData.phone_number,
        email: initialData.email,
        status: initialData.status,
      });
    } else {
      setForm({
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        venue: '',
        exhibition_type: 0,
        phone_number: 0,
        email: '',
        status: true,
      });
    }
  }, [initialData]);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const types = await ExhibitionTypeService.getTypes(); 
        setTypes(types); 
      } catch {
        toast.error('Failed to load exhibition types');
      }
    };
    fetchTypes();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type  } = e.target;
    setForm({
      ...form,
        [name]: type === 'checkbox' ? e.target.checked : value,
    });

    // Clear field error if user changes value
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!form.title) newErrors.title = 'Title is required';
    if (!form.start_date) newErrors.start_date = 'Start date is required';
    if (!form.end_date) newErrors.end_date = 'End date is required';
    if (!form.exhibition_type) newErrors.exhibition_type = 'Exhibition type is required';
    return newErrors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      if (initialData) {
        await ExhibitionService.update(initialData.id, form);
        toast.success('Exhibition updated successfully!');
      } else {
        await ExhibitionService.create(form);
        toast.success('Exhibition created successfully!');
      }
      onSuccess(); // refetch
      toggle();
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {initialData ? 'Edit' : 'Add'} Exhibition
      </ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="title">Title</Label>
          <Input
            name="title"
            value={form.title}
            onChange={handleChange}
            invalid={!!errors.title}
          />
          <FormFeedback>{errors.title}</FormFeedback>
        </FormGroup>

        <FormGroup>
          <Label for="description">Description</Label>
          <Input
            name="description"
            value={form.description}
            onChange={handleChange}
            type="textarea"
          />
        </FormGroup>

        <div className="row">
        <FormGroup className="col-md-6">
            <Label for="start_date">Start Date</Label>
            <Input
            type="date"
            name="start_date"
            value={form.start_date}
            onChange={handleChange}
            invalid={!!errors.start_date}
            />
            <FormFeedback>{errors.start_date}</FormFeedback>
        </FormGroup>

        <FormGroup className="col-md-6">
            <Label for="end_date">End Date</Label>
            <Input
            type="date"
            name="end_date"
            value={form.end_date}
            onChange={handleChange}
            invalid={!!errors.end_date}
            />
            <FormFeedback>{errors.end_date}</FormFeedback>
        </FormGroup>
        </div>


        <FormGroup>
          <Label for="venue">Venue</Label>
          <Input
            name="venue"
            value={form.venue}
            onChange={handleChange}
          />
        </FormGroup>

         <div className="row">
        <FormGroup className="col-md-6">
            <Label for="phone_number">Telephone</Label>
            <Input
            type="text"
            name="phone_number"
            value={form.phone_number}
            onChange={handleChange}
            invalid={!!errors.phone_number}
            />
            <FormFeedback>{errors.phone_number}</FormFeedback>
        </FormGroup>

        <FormGroup className="col-md-6">
            <Label for="email">Email</Label>
            <Input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            invalid={!!errors.email}
            />
            <FormFeedback>{errors.email}</FormFeedback>
        </FormGroup>
        </div>

       <div className="row align-items-end">
        <FormGroup className="col-md-8">
            <Label for="exhibition_type">Exhibition Type</Label>
            <Input
            type="select"
            name="exhibition_type"
            value={form.exhibition_type}
            onChange={handleChange}
            invalid={!!errors.exhibition_type}
            >
            <option value="">-- Select --</option>
            {types.map((type) => (
                <option key={type.id} value={type.id}>
                {type.name}
                </option>
            ))}
            </Input>
            <FormFeedback>{errors.exhibition_type}</FormFeedback>
        </FormGroup>



        <FormGroup check className="form-switch col-md-4 mt-3 mt-md-0">
            <Input
            type="checkbox"
            name="status"
            checked={form.status}
            onChange={handleChange}
            />
            <Label check>Status</Label>
        </FormGroup>
        </div>


        <div className="text-end mt-4">
          <Button color="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? <Spinner size="sm" /> : initialData ? 'Update' : 'Create'}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ExhibitionModal;
